import { isEmpty } from "@mongez/supportive-is";
import { parseError, useSubmitProps } from "core/shared";
import { action } from "framework/config/action";
import { URLS } from "framework/config/urls";

export function useLogin({ onSuccess, onError }: useSubmitProps) {
  const submit = action(URLS.auth.login);

  const login = async ({ values, form }) => {
    const response = await submit(values);

    form.submitting(false);

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { login };
}

export function useRegister({ onSuccess, onError }: useSubmitProps) {
  const submit = action(URLS.auth.register);

  const register = async ({ values, form }) => {
    const response = await submit(values);

    form.submitting(false);

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { register };
}

export function useRegisterVerifyCode({ onSuccess, onError }: useSubmitProps) {
  const submit = action(URLS.auth.verifyRegister);

  const verifyRegister = async ({ values, form }) => {
    const response = await submit(values);

    form.submitting(false);

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { verifyRegister };
}

export function useLogout({ onSuccess, onError }: useSubmitProps) {
  const logoutAction = action(URLS.auth.logout);

  const logout = async () => {
    const response = await logoutAction();

    if (response.isOK) {
      onSuccess?.(response.data);
      window.location.href = URLS.home;
    } else {
      onError?.(parseError(response));
    }
  };

  return { logout };
}

/**
 * Send forget password request hook
 */
export function useForgetPassword({
  onSubmit,
  onSuccess,
  onError,
}: useSubmitProps) {
  const submit = action(URLS.auth.forgetPassword);

  const forgetPasswordSubmit = async ({ values, form }) => {
    const response = await submit(values);
    onSubmit?.({ values, form });

    form.submitting(false);

    if (response.isOK) {
      onSuccess?.(values);
    } else {
      onError?.(parseError(response));
    }
  };

  return { forgetPasswordSubmit };
}

/**
 * Verify forget password code hook
 */
export function useVerifyForgetPasswordOTP({
  onSubmit,
  onSuccess,
  onError,
}: useSubmitProps) {
  const submit = action(URLS.auth.verifyCode);

  const verifyForgetPasswordOTPSubmit = async ({ values, form }) => {
    const data = onSubmit?.(values);

    const response = await submit({
      ...values,
    });

    form.submitting(false);

    if (response.isOK) {
      onSuccess?.(isEmpty(response.data) ? data : response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { verifyForgetPasswordOTPSubmit };
}

/**
 * Reset password hook
 */
export function useResetPassword({
  onSubmit,
  onSuccess,
  onError,
}: useSubmitProps) {
  const submit = action(URLS.auth.resetPassword);

  const resetPasswordSubmit = async ({ values, form }) => {
    const { email, code } = onSubmit?.(values);

    if (!email || !code) return form.submitting(false);

    const response = await submit({
      ...values,
      email,
      code,
    });

    form.submitting(false);

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { resetPasswordSubmit };
}

export function useLoginWithGoogle({ onSuccess, onError }) {
  const loginByGoogleAction = action(URLS.auth.loginByGoogle);

  const handleGoogleLogin = async (credentialResponse) => {
    const response = await loginByGoogleAction({
      credential: credentialResponse.credential,
    });

    if (response.isOK) {
      onSuccess?.(response.data);
    } else {
      onError?.(parseError(response));
    }
  };

  return { handleGoogleLogin };
}
